import { computed, ref } from 'vue'

import { SmartProcessingAudit } from '../../../../@core/processing/domain/entities/SmartProcessingAudit'
import { ISmartProcessingAuditRepository } from '../../../../@core/processing/domain/repositories/ISmartProcessingAudit.repository'
import { SmartProcessingAuditRepository } from '../../../../@core/processing/infra/repositories/SmartProcessingAudit.repository'
import { InterestVariableEnum } from '../../../../@core/@shared/domain/enums/InterestVariable.enum'
import { interestVariableLabel } from '../../@shared/constants/InterestVariableLabel'
import { registryApi } from '../../../../services/clients/AxiosClients'
import { useMessages } from '../../../../composables/useMessages'
import i18n from '../../../../VueI18n'

export class SmartProcessingAuditViewModel {
	private _repository: ISmartProcessingAuditRepository
	private _messages = useMessages()
	private _loadedStratumId: string | null = null

	audits = ref<SmartProcessingAudit[]>([])
	isLoading = ref(false)
	hasFailed = ref(false)
	selectedInterestVariable = ref<InterestVariableEnum | null>(null)

	constructor(repository?: ISmartProcessingAuditRepository) {
		this._repository = repository ?? new SmartProcessingAuditRepository(registryApi)
	}

	interestVariableOptions = computed(() =>
		this.audits.value.map((audit) => ({
			value: audit.interestVariable,
			label: interestVariableLabel(audit.interestVariable),
			highlight: audit.hasSamplingGains,
		})),
	)

	/**
	 * O Dropdown aceita string/number/array, nunca null -- fica aqui, e nao no
	 * componente, para que qualquer consumidor do viewmodel receba ja coagido.
	 */
	selectedInterestVariableValue = computed<string | undefined>(
		() => this.selectedInterestVariable.value ?? undefined,
	)

	selectedAudit = computed(
		() =>
			this.audits.value.find(
				(audit) => audit.interestVariable === this.selectedInterestVariable.value,
			) ?? null,
	)

	/**
	 * Coagidos para 0 quando nao ha auditoria selecionada, para o template
	 * decidir a exibicao com um simples `> 0`, sem tratar nulo.
	 */
	gainedParcels = computed(() => this.selectedAudit.value?.gainedParcels ?? 0)

	samplingErrorPercentageReduction = computed(
		() => this.selectedAudit.value?.samplingErrorPercentageReduction ?? 0,
	)

	formattedSamplingErrorPercentageReduction = computed(
		() => this.selectedAudit.value?.formattedSamplingErrorPercentageReduction ?? '0.00',
	)

	isEmpty = computed(
		() => !this.isLoading.value && !this.hasFailed.value && this.audits.value.length === 0,
	)

	selectInterestVariable(interestVariable: InterestVariableEnum) {
		this.selectedInterestVariable.value = interestVariable
	}

	/**
	 * Idempotente por estrato: a aba smart pode ser reaberta varias vezes sem
	 * refazer a request. `force` existe para o fim do processamento, quando o
	 * mesmo id passa a ter dados novos.
	 */
	async load(forestInventoryStratumId: string, force: boolean = false) {
		if (!forestInventoryStratumId) return
		if (!force && this._loadedStratumId === forestInventoryStratumId) return

		this.isLoading.value = true
		this.hasFailed.value = false

		try {
			const audits = await this._repository.getByForestInventoryStratumId(forestInventoryStratumId)

			this.audits.value = audits
			this._loadedStratumId = forestInventoryStratumId
			this.selectedInterestVariable.value = audits.length ? audits[0].interestVariable : null
		} catch (erro) {
			console.error('[SmartProcessingAudit] falha ao recuperar dados de auditoria:', erro)
			this.audits.value = []
			this.selectedInterestVariable.value = null
			this._loadedStratumId = null
			this.hasFailed.value = true
			this._messages.addMessage({
				title: i18n.t('erro') as string,
				description: i18n.t('falhaAoCarregarAuditoriaSmart') as string,
				type: 'error',
			})
		} finally {
			this.isLoading.value = false
		}
	}

	reset() {
		this.audits.value = []
		this.selectedInterestVariable.value = null
		this.hasFailed.value = false
		this._loadedStratumId = null
	}
}
